import moment from 'moment'
import { DoctorProperties, DoctorFlags, RelationDivision } from '@/infrastructure/network/Monitaro/schema/models'
import HospitalEntity, { IHospitalProps } from '@/entities/Hospital'
import LogEntity, { ILogProps } from '@/entities/Log'

export interface IDoctorProps {
  id?: string
  hospitals?: IHospitalProps[]
  division?: RelationDivision
  occupation?: string | null
  role?: string | null
  name: string | null
  username: string | null
  login: string | null
  fcmToken: string | null
  uuid: string | null
  fullname: string | null
  kana: string | null
  organization: string | null
  properties: DoctorProperties
  flags: DoctorFlags
  provider: any | null
  activation: boolean | null
  log?: ILogProps | null
  invitedAt: string | null
  activateAt: string | null
  createdAt: string | null
  updatedAt: string | null
  hospitalId?: string | null
  divisionId?: string | null
  areas?: string[]
}

export interface IDoctorQueryProps {
  roles: string[] | null
  occupations: string[] | null
  divisions: string[] | null
}

export default class DoctorEntity {
  private _props: IDoctorProps

  constructor(props?: IDoctorProps | null) {
    this._props = EmptyDoctorPropsFactory(props)
  }

  get props(): IDoctorProps {
    return this._props
  }

  get clone(): IDoctorProps {
    return structuredClone(this._props)
  }

  get id(): string | undefined {
    return this._props.id
  }

  get hospitals(): HospitalEntity[] | undefined {
    return this._props.hospitals ? this._props.hospitals.map((prop) => new HospitalEntity(prop)) : []
  }

  get division(): RelationDivision | undefined {
    return this._props.division
  }

  get occupation(): string | null | undefined {
    return this._props.occupation
  }

  get role(): string {
    return roles.find((prop) => prop.value === this._props.role)?.text || ''
  }

  get superuser(): boolean {
    return this._props.flags.superuser || false
  }

  get name(): string | null {
    return this._props.name
  }

  get username(): string | null {
    return this._props.username
  }

  get login(): string | null {
    return this._props.login
  }

  get fcmToken(): string | null {
    return this._props.fcmToken
  }

  get uuid(): string | null {
    return this._props.uuid
  }

  get fullname(): string | null {
    return this._props.fullname
  }

  get kana(): string | null {
    return this._props.kana
  }

  get organization(): string | null {
    return this._props.organization
  }

  get flags(): DoctorFlags | null | undefined {
    return this._props.flags
  }

  get properties(): DoctorProperties | null | undefined {
    return this._props.properties
  }

  get activation(): string {
    return this._props.activation ? '済' : ''
  }

  get log(): LogEntity | null | undefined {
    return new LogEntity(this._props.log)
  }

  get actionedAt(): string | null {
    return this.log?.createdAt || (this._props.properties?.actioned ? moment(this._props.properties.actioned).format('YYYY-MM-DD HH:mm') : null)
  }

  get fromNow(): string | null {
    moment.locale('ja')
    return this.actionedAt ? moment(this.actionedAt).fromNow() : null
  }

  get invitedOn(): string {
    return this._props.invitedAt ? moment(this._props.invitedAt).format('YYYY/MM/DD') : ''
  }

  get activateAt(): string | null {
    return this._props.activateAt
  }

  get createdAt(): string | null {
    return this._props.createdAt
  }

  get updatedAt(): string | null {
    return this._props.updatedAt
  }

  get areas(): string[] | undefined {
    return this._props.areas
  }

  get guard(): boolean {
    return this._props.role === 'guard'
  }
}

export const headers = [
  { text: '名前', value: 'name', sortable: true, class: 'space-normal', style: { 'min-width': '100px' } },
  { text: '権限', value: 'role', sortable: true },
  { text: '部署', value: 'division', sortable: true, class: 'space-normal', style: { 'min-width': '60px' } },
  { text: '職種', value: 'occupation', sortable: true },
  { text: 'メールアドレス', value: 'username', sortable: true },
  { text: '登録済', value: 'activation', class: 'text-center', sortable: true },
  { text: '招待日', value: 'createdAt', sortable: true },
  { text: '最終操作日時', value: 'actionedAt', class: 'text-right' },
  { text: '', value: 'remove' }
]

export const doctors = [{ text: '名前', value: 'doctor' }]

export const messages = [
  { text: '', value: 'checked' },
  { text: '名前', value: 'name' },
  { text: '部署', value: 'division' },
  { text: '権限', value: 'role' },
  { text: '職種', value: 'occupation' }
]

export const hospitals = [
  { text: '', value: 'checked' },
  { text: '名前', value: 'name' },
  { text: '病院名', value: 'organization' },
  { text: '部署', value: 'division' },
  { text: '職種', value: 'occupation' }
]

export const roles = [
  { text: '全メニュー', value: 'admin' },
  { text: '限定メニュー', value: 'user' }
  // { text: '記帳管理のみ', value: 'guard' }
]

export const occupations = [
  { text: '医師', value: '医師' },
  { text: '薬剤師', value: '薬剤師' },
  { text: '事務', value: '事務' },
  { text: '看護師', value: '看護師' },
  { text: '技師・士業', value: '技師・士業' },
  { text: 'その他', value: 'その他' }
]

export const weeks = [
  { text: '月', value: '月' },
  { text: '火', value: '火' },
  { text: '水', value: '水' },
  { text: '木', value: '木' },
  { text: '金', value: '金' },
  { text: '土', value: '土' },
  { text: '日', value: '日' }
]

export const frames = [
  { text: 'なし', value: null },
  { text: '3分', value: 3 },
  { text: '5分', value: 5 },
  { text: '10分', value: 10 },
  { text: '15分', value: 15 },
  { text: '20分', value: 20 },
  { text: '30分', value: 30 },
  { text: '60分', value: 60 }
]
export const buttons = [
  { text: '終日可', value: 1, color: 'secondary', icon: 'icon-check-circle-solid.svg' },
  { text: '終日不可', value: 2, color: 'alert', icon: 'icon-times-circle.svg' },
  { text: '時間を設定する', value: 3, color: 'primary', icon: 'icon-clock-three-regular.svg' }
]
export const rules = [
  { day: '月', value: 1, times: [] },
  { day: '火', value: 1, times: [] },
  { day: '水', value: 1, times: [] },
  { day: '木', value: 1, times: [] },
  { day: '金', value: 1, times: [] },
  { day: '土', value: 1, times: [] },
  { day: '日', value: 1, times: [] }
]

export const EmptyDoctorQueryPropsFactory = (props?: Partial<IDoctorQueryProps> | null): IDoctorQueryProps => ({
  roles: null,
  occupations: null,
  divisions: null,
  ...props
})

export const EmptyDoctorPropsFactory = (props?: Partial<IDoctorProps> | null): IDoctorProps => ({
  hospitals: [],
  role: 'admin',
  name: null,
  username: null,
  login: null,
  fcmToken: null,
  uuid: null,
  fullname: null,
  kana: null,
  provider: null,
  organization: null,
  activation: null,
  invitedAt: null,
  activateAt: null,
  createdAt: null,
  updatedAt: null,
  areas: [],
  ...props,
  properties: EmptyDoctorPropertiesFactory({ ...props?.properties }),
  flags: EmptyDoctorFlagsFactory({ ...props?.flags })
})

export const EmptyDoctorPropertiesFactory = (props?: Partial<DoctorProperties> | null): DoctorProperties => ({
  actioned: null,
  rules: null,
  frame: null,
  request: null,
  ...props
})

export const EmptyDoctorFlagsFactory = (props?: Partial<DoctorFlags> | null): DoctorFlags => ({
  owner: false,
  chat: false,
  appoint: false,
  notification: false,
  report: false,
  calendar: false,
  duplicate: false,
  attendance: false,
  attended: null,
  ...props
})
